export const PermissionTypes = {
  LOCATION: "location",
  NOTIFICATIONS: "notifications",
} as const
export type PermissionType = typeof PermissionTypes[keyof typeof PermissionTypes]
const validTypes = Object.values(PermissionTypes) as PermissionType[]

export type PermissionStatus = "granted" | "denied" | "undetermined"
const validStatuses: PermissionStatus[] = ["granted", "denied", "undetermined"]

export type PlatformType = "web" | "ios" | "android"

export interface PermissionRecord {
  status: PermissionStatus
  lastUpdated?: string // ISO string
}
export interface NotificationPermissionRecord extends PermissionRecord {
  pushToken?: string
}

export interface DevicePermissions {
  [PermissionTypes.LOCATION]: PermissionRecord
  [PermissionTypes.NOTIFICATIONS]: NotificationPermissionRecord
}

export type AppPermissions = {
  [deviceId: string]: DevicePermissions
}


const expoPushTokenRegex = /^ExponentPushToken\[[A-Za-z0-9-_]+\]$/

const isValidExpoPushToken = (token: string): boolean => {
  return expoPushTokenRegex.test(token)
}

const isValidPermissionRecord = (record: PermissionRecord): boolean => {
  return (
    validStatuses.includes(record.status) &&
    typeof record.lastUpdated === "string" && !isNaN(Date.parse(record.lastUpdated))
  )
}

const isValidNotificationPermissionRecord = (record: NotificationPermissionRecord): boolean => {
  return (
    isValidPermissionRecord(record) &&
    (!record.pushToken || isValidExpoPushToken(record.pushToken))
  )
}

const isValidDevicePermissions = (permissions: DevicePermissions): boolean => {
  return validTypes.every(type => type in permissions) &&
    isValidPermissionRecord(permissions[PermissionTypes.LOCATION]) &&
    isValidNotificationPermissionRecord(permissions[PermissionTypes.NOTIFICATIONS])
}

export const isValidAppPermissions = (permissions: AppPermissions): boolean => {
  return Object.values(permissions).every(isValidDevicePermissions)
}